// Jalaali date utilities for analytics
// Embedded jalaali conversion functions for browser compatibility (Rollup)

const breaks = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210,
    1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178]

function div(a: number, b: number): number {
    return ~~(a / b)
}

function mod(a: number, b: number): number {
    return a - ~~(a / b) * b
}

function g2d(gy: number, gm: number, gd: number): number {
    let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4)
        + div(153 * mod(gm + 9, 12) + 2, 5)
        + gd - 34840408
    d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752
    return d
}

function jalCal(jy: number, withoutLeap?: boolean): { leap: number, gy: number, march: number } {
    const bl = breaks.length
    let gy = jy + 621
    let leapJ = -14
    let jp = breaks[0]
    let jm, jump, leap, leapG, march, n, i

    for (i = 1; i < bl; i += 1) {
        jm = breaks[i]
        jump = jm - jp
        if (jy < jm) break
        leapJ = leapJ + div(jump, 33) * 8 + div(mod(jump, 33), 4)
        jp = jm
    }
    n = jy - jp

    leapJ = leapJ + div(n, 33) * 8 + div(mod(n, 33) + 3, 4)
    if (mod(jump!, 33) === 4 && jump! - n === 4) leapJ += 1

    leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150
    march = 20 + leapJ - leapG

    if (withoutLeap) return { leap: 0, gy: gy, march: march }

    if (jump! - n < 6) n = n - jump! + div(jump! + 4, 33) * 33
    leap = mod(mod(n + 1, 33) - 1, 4)
    if (leap === -1) leap = 4

    return { leap: leap, gy: gy, march: march }
}

function j2d(jy: number, jm: number, jd: number): number {
    const r = jalCal(jy, true)
    return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1
}

function d2g(jdn: number): { gy: number, gm: number, gd: number } {
    let j = 4 * jdn + 139361631
    j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908
    const i = div(mod(j, 1461), 4) * 5 + 308
    const gd = div(mod(i, 153), 5) + 1
    const gm = mod(div(i, 153), 12) + 1
    const gy = div(j, 1461) - 100100 + div(8 - gm, 6)
    return { gy, gm, gd }
}

function d2j(jdn: number): { jy: number, jm: number, jd: number } {
    const gy = d2g(jdn).gy
    let jy = gy - 621
    const r = jalCal(jy, false)
    const jdn1f = g2d(gy, 3, r.march)
    let jd, jm, k

    k = jdn - jdn1f
    if (k >= 0) {
        if (k <= 185) {
            jm = 1 + div(k, 31)
            jd = mod(k, 31) + 1
            return { jy, jm, jd }
        } else {
            k -= 186
        }
    } else {
        jy -= 1
        k += 179
        if (r.leap === 1) k += 1
    }
    jm = 7 + div(k, 30)
    jd = mod(k, 30) + 1
    return { jy, jm, jd }
}

function toJalaali(date: Date): { jy: number, jm: number, jd: number } {
    const gd = date.getDate()
    const gm = date.getMonth() + 1
    const gy = date.getFullYear()
    return d2j(g2d(gy, gm, gd))
}

function toGregorian(jy: number, jm: number, jd: number): { gy: number, gm: number, gd: number } {
    return d2g(j2d(jy, jm, jd))
}

function isValidJalaaliDate(jy: number, jm: number, jd: number): boolean {
    return jy >= -61 && jy <= 3177 &&
        jm >= 1 && jm <= 12 &&
        jd >= 1 && jd <= jalaaliMonthLength(jy, jm)
}

function jalaaliMonthLength(jy: number, jm: number): number {
    if (jm <= 6) return 31
    if (jm <= 11) return 30
    const bl = breaks.length
    let jp = breaks[0]
    let jmBreak, jump, n, i

    for (i = 1; i < bl; i += 1) {
        jmBreak = breaks[i]
        jump = jmBreak - jp
        if (jy < jmBreak) break
        jp = jmBreak
    }
    n = jy - jp

    if (jump! - n < 6) n = n - jump! + div(jump! + 4, 33) * 33
    let leap = mod(mod(n + 1, 33) - 1, 4)
    if (leap === -1) leap = 4

    return leap === 0 ? 30 : 29
}

// ===== Utility exports =====

const months = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
]

const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

export function toPersianNumbers(str: string): string {
    return str.replace(/[0-9]/g, (d) => persianDigits[parseInt(d)])
}

export function toJalaaliString(date: Date | string, format: 'short' | 'full' = 'short'): string {
    const d = typeof date === 'string' ? new Date(date) : date
    const jDate = toJalaali(d)

    if (format === 'full') {
        return toPersianNumbers(`${jDate.jd} ${months[jDate.jm - 1]} ${jDate.jy}`)
    }
    // short format: jy/jm/jd
    const month = jDate.jm.toString().padStart(2, '0')
    const day = jDate.jd.toString().padStart(2, '0')
    return toPersianNumbers(`${jDate.jy}/${month}/${day}`)
}

export function toJalaaliShortString(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date
    const jDate = toJalaali(d)
    const month = jDate.jm.toString().padStart(2, '0')
    const day = jDate.jd.toString().padStart(2, '0')
    // MM/DD format for chart labels
    return toPersianNumbers(`${month}/${day}`)
}

export type DateRange = 'last24h' | 'lastWeek' | 'lastMonth' | 'custom'

export interface DateRangeOption {
    id: DateRange
    label: string
    days: number
}

export const dateRangeOptions: DateRangeOption[] = [
    { id: 'last24h', label: '۲۴ ساعت گذشته', days: 1 },
    { id: 'lastWeek', label: 'هفته گذشته', days: 7 },
    { id: 'lastMonth', label: 'ماه گذشته', days: 30 },
    { id: 'custom', label: 'انتخاب تاریخ', days: 0 }
]

export function getDateRangeDays(range: DateRange): number {
    const option = dateRangeOptions.find(o => o.id === range)
    return option?.days || 30
}

export function jalaaliToGregorian(jy: number, jm: number, jd: number): Date {
    const g = toGregorian(jy, jm, jd)
    return new Date(g.gy, g.gm - 1, g.gd)
}

export function parseJalaaliDate(dateStr: string): { jy: number, jm: number, jd: number } | null {
    // Accept format: YYYY/MM/DD or YYYY-MM-DD (Persian or Arabic numerals)
    const normalized = dateStr
        .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
        .replace(/\//g, '-')

    const parts = normalized.split('-').map(Number)
    if (parts.length !== 3 || parts.some(isNaN)) return null

    const [jy, jm, jd] = parts
    if (isValidJalaaliDate(jy, jm, jd)) {
        return { jy, jm, jd }
    }
    return null
}
